import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Upload,
  Button,
  Space,
  Input,
  Image,
  Timeline,
  Alert,
  message,
  Modal,
  List,
  Pagination,
  Tag,
  Skeleton,
} from 'antd';
import {
  CloudUploadOutlined,
  PlayCircleOutlined,
  LoadingOutlined,
  CheckCircleTwoTone,
  CameraOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import ModuleNav from './ModuleNav';
import { listSwatches, processTryOn } from '../../services/aiImageMaskingApi';
import { listAgents } from '../../services/aiImageMaskingApi';

const { Title, Paragraph, Text } = Typography;
const { TextArea, Search } = Input;

const TryBeforeYouBuy = () => {
  const [swatches, setSwatches] = useState([]);
  const [selectedSwatch, setSelectedSwatch] = useState(null);
  const [environmentFile, setEnvironmentFile] = useState([]);
  const [environmentPreview, setEnvironmentPreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [agentTag, setAgentTag] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [resultPreview, setResultPreview] = useState(null);
  const [jobMeta, setJobMeta] = useState(null);
  const [debugVisible, setDebugVisible] = useState(false);
  const [debugPayload, setDebugPayload] = useState(null);
  const [swatchPage, setSwatchPage] = useState(1);
  const [swatchPageSize, setSwatchPageSize] = useState(8);
  const [swatchTotal, setSwatchTotal] = useState(0);
  const [swatchSearch, setSwatchSearch] = useState('');
  const [swatchLoading, setSwatchLoading] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [quickTrySwatch, setQuickTrySwatch] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraPreview, setCameraPreview] = useState(null);
  const [cameraBlob, setCameraBlob] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [userId, setUserId] = useState('');
  const [existingTryOn, setExistingTryOn] = useState(null);
  const [existingModalVisible, setExistingModalVisible] = useState(false);
  const [pendingForce, setPendingForce] = useState(null);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraCanvasRef = useRef(null);

  useEffect(() => {
    const loadSwatches = async () => {
      setSwatchLoading(true);
      try {
        const data = await listSwatches({
          page: swatchPage,
          page_size: swatchPageSize,
          search: swatchSearch || undefined,
        });
        setSwatches(Array.isArray(data?.items) ? data.items : []);
        setSwatchTotal(data?.total || 0);
      } catch (error) {
        message.error(error.message || 'Unable to fetch swatches');
      } finally {
        setSwatchLoading(false);
      }
    };
    loadSwatches();
  }, [swatchPage, swatchPageSize, swatchSearch]);

  const trimmedUserId = userId.trim();

  const applyEnvironmentFile = (file) => {
    if (!file) {
      return;
    }
    if (environmentPreview) {
      URL.revokeObjectURL(environmentPreview);
    }
    const previewUrl = URL.createObjectURL(file);
    file.preview = previewUrl;
    setEnvironmentPreview(previewUrl);
    setEnvironmentFile([file]);
  };

  const uploadProps = {
    multiple: false,
    fileList: environmentFile,
    beforeUpload: (file) => {
      applyEnvironmentFile(file);
      return false;
    },
    onRemove: () => {
      if (environmentPreview) {
        URL.revokeObjectURL(environmentPreview);
      }
      setEnvironmentPreview(null);
      setEnvironmentFile([]);
    },
    accept: '.png,.jpg,.jpeg,.webp',
  };

  useEffect(() => {
    return () => {
      if (environmentPreview) {
        URL.revokeObjectURL(environmentPreview);
      }
    };
  }, [environmentPreview]);

  useEffect(() => {
    if (showCameraModal) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCameraModal]);

  useEffect(() => {
    if (cameraStream && cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  const handleManualInput = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      applyEnvironmentFile(file);
    }
    event.target.value = '';
  };

  const handleSwatchSearch = (value) => {
    setSwatchPage(1);
    setSwatchSearch(value.trim());
  };

  const environmentPreviewNode = useMemo(() => {
    if (!environmentPreview) {
      return null;
    }
    return (
      <div className="mt-3">
        <Text strong>Uploaded preview</Text>
        <Image
          src={environmentPreview}
          alt="Uploaded lifestyle preview"
          style={{ width: '100%', marginTop: 12, borderRadius: 12 }}
        />
      </div>
    );
  }, [environmentPreview]);

  const submitTryOn = useCallback(
    async ({ swatchId, forceOverwrite = false, context = 'primary' }) => {
      if (!environmentFile.length) {
        message.warning('Upload a lifestyle image to continue');
        return null;
      }
      if (!swatchId) {
        message.warning('Select a swatch');
        return null;
      }
      if (!trimmedUserId) {
        message.warning('Enter a user ID to continue');
        return null;
      }
      try {
        const response = await processTryOn({
          environmentFile: environmentFile[0],
          swatchId,
          prompt,
          userId: trimmedUserId,
          agentTag: agentTag || undefined,
          forceOverwrite,
        });
        const preview =
          response?.overlay_url ||
          response?.preview_url ||
          response?.composited_image_url ||
          (response?.image_base64 ? `data:image/png;base64,${response.image_base64}` : null);
        setResultPreview(preview);
        setJobMeta({
          job_id: response?.job_id,
          model: response?.model,
          status: response?.status || 'completed',
          openai: response?.openai,
          user_id: response?.user_id,
          gallery_entry: response?.gallery_entry,
          agent_tag: response?.agent_tag || (agentTag || ''),
        });
        setDebugPayload(response?.openai?.debug || null);
        setExistingTryOn(null);
        setPendingForce(null);
        setExistingModalVisible(false);
        message.success(forceOverwrite ? 'Overlay reapplied' : 'Overlay generated');
        return response;
      } catch (error) {
        if (error.code === 'tryon_exists') {
          setExistingTryOn(error.payload?.existing || null);
          setPendingForce({ swatchId, context });
          setExistingModalVisible(true);
          return null;
        }
        throw error;
      }
    },
    [agentTag, environmentFile, prompt, trimmedUserId]
  );

  const handleTryOn = async ({ forceOverwrite = false } = {}) => {
    if (!environmentFile.length) {
      message.warning('Upload a lifestyle image to continue');
      return;
    }
    if (!selectedSwatch) {
      message.warning('Select a swatch');
      return;
    }
    setProcessing(true);
    try {
      await submitTryOn({ swatchId: selectedSwatch, forceOverwrite, context: 'primary' });
    } catch (error) {
      message.error(error.message || 'Unable to process try-on');
    } finally {
      setProcessing(false);
    }
  };

  const handleCardTryOn = async (swatchId) => {
    if (!environmentFile.length) {
      setShowPhotoModal(true);
      return;
    }
    setQuickTrySwatch(swatchId);
    try {
      await submitTryOn({ swatchId, context: 'card' });
    } catch (error) {
      message.error(error.message || 'Unable to process try-on');
    } finally {
      setQuickTrySwatch(null);
    }
  };

  const handleUseExistingResult = () => {
    if (existingTryOn?.overlay_url) {
      setResultPreview(existingTryOn.overlay_url);
      message.info('Showing existing overlay from gallery');
    }
    setExistingModalVisible(false);
    setPendingForce(null);
  };

  const handleConfirmReapply = async () => {
    const swatchId = pendingForce?.swatchId;
    if (!swatchId) {
      setExistingModalVisible(false);
      return;
    }
    setExistingModalVisible(false);
    if (pendingForce?.context === 'card') {
      setQuickTrySwatch(swatchId);
      try {
        await submitTryOn({ swatchId, forceOverwrite: true, context: 'card' });
      } catch (error) {
        message.error(error.message || 'Unable to reapply overlay');
      } finally {
        setQuickTrySwatch(null);
      }
    } else {
      setProcessing(true);
      try {
        await submitTryOn({ swatchId, forceOverwrite: true, context: 'primary' });
      } catch (error) {
        message.error(error.message || 'Unable to reapply overlay');
      } finally {
        setProcessing(false);
      }
    }
  };

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera access is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      setCameraStream(stream);
      setCameraError(null);
      setCameraPreview(null);
      setCameraBlob(null);
      setIsCapturing(false);
    } catch (error) {
      console.error('Camera error', error);
      setCameraError(error.message || 'Unable to access camera');
      setCameraStream(null);
    }
  }, []);

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    if (cameraPreview) {
      URL.revokeObjectURL(cameraPreview);
    }
    setCameraStream(null);
    setCameraPreview(null);
    setCameraBlob(null);
    setIsCapturing(false);
  };

  const capturePhoto = () => {
    if (!cameraVideoRef.current || !cameraCanvasRef.current) {
      return;
    }
    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    canvas.width = video.videoWidth || 1080;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setIsCapturing(true);
    canvas.toBlob(
      (blob) => {
        if (cameraPreview) {
          URL.revokeObjectURL(cameraPreview);
        }
        const url = blob ? URL.createObjectURL(blob) : null;
        setCameraBlob(blob);
        setCameraPreview(url);
        setIsCapturing(false);
        if (video && video.pause) {
          video.pause();
        }
      },
      'image/jpeg',
      0.95
    );
  };

  const confirmCameraPhoto = () => {
    if (!cameraBlob) {
      return;
    }
    const file = new File([cameraBlob], `camera-${Date.now()}.jpg`, { type: cameraBlob.type || 'image/jpeg' });
    applyEnvironmentFile(file);
    setShowCameraModal(false);
    setShowPhotoModal(false);
  };

  useEffect(() => {
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        const data = await listAgents();
        setAgents(Array.isArray(data?.items) ? data.items : []);
      } catch (error) {
        message.error(error.message || 'Unable to load agents');
      } finally {
        setAgentsLoading(false);
      }
    };
    loadAgents();
  }, []);

  const availableAgentTags = useMemo(() => {
    const tagMap = new Map();
    agents.forEach((agent) => {
      (agent.tags || []).forEach((tag) => {
        const trimmed = (tag || '').trim();
        if (trimmed) {
          const key = trimmed.toLowerCase();
          if (!tagMap.has(key)) {
            tagMap.set(key, trimmed);
          }
        }
      });
    });
    return Array.from(tagMap.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [agents]);

  const debugRequest =
    debugPayload?.request ||
    debugPayload?.debug?.request ||
    ((debugPayload?.method || debugPayload?.url || debugPayload?.headers || debugPayload?.fields || debugPayload?.files)
      ? debugPayload
      : null) ||
    null;
  const debugResponse =
    debugPayload?.response || debugPayload?.debug?.response || debugRequest?.response || null;
  const hasDebugTransportData = Boolean(debugRequest || debugResponse);
  const debugMethod = debugRequest?.method || (debugRequest?.sdk ? 'SDK call' : null) || 'N/A';
  const debugUrl =
    debugRequest?.url || (debugRequest?.sdk ? 'Google GenAI SDK (no direct HTTP URL)' : null) || 'N/A';
  const debugHeaders =
    debugRequest?.headers || (debugRequest?.sdk ? { note: 'Headers are managed internally by the SDK' } : {});
  const debugFields =
    debugRequest?.body ||
    debugRequest?.fields ||
    debugRequest?.payload ||
    (debugRequest?.sdk
      ? {
          sdk: true,
          model: debugRequest?.model || debugPayload?.model || null,
          generation_config: debugRequest?.generation_config || null,
          prompt_preview: debugRequest?.prompt || null,
        }
      : {});
  const debugFiles = debugRequest?.files || debugRequest?.parts || [];
  const failedAttempts =
    debugPayload?.debug?.failed_attempts ||
    debugPayload?.failed_attempts ||
    [];
  const debugModel = debugPayload?.debug?.request?.model || debugRequest?.model || debugPayload?.model || null;
  const resolvedDebugError =
    debugPayload?.error ||
    debugPayload?.debug?.error ||
    debugResponse?.error ||
    debugResponse?.json?.error?.message ||
    debugResponse?.json?.error ||
    null;
  const resolvedDebugErrorType = debugResponse?.error_type || debugResponse?.json?.error?.type || null;

  return (
    <div className="ai-image-masking-page">
      <ModuleNav activeKey="try" />
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={galleryInputRef}
        onChange={handleManualInput}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        ref={cameraInputRef}
        onChange={handleManualInput}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} xl={14}>
          <Card title="Try Before You Buy" className="shadow-sm" bodyStyle={{ padding: '24px' }}>
            <Space direction="vertical" size="large" className="w-100">
              <div>
                <Text strong>0. Provide a user ID</Text>
                <Input
                  className="mt-2"
                  placeholder="E.g., customer email or internal user identifier"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  maxLength={120}
                />
                <Paragraph type="secondary" className="mb-0 mt-1">
                  Overlays are stored per user so you can compare past tryouts.
                </Paragraph>
              </div>
              <div>
                <Text strong>1. Upload a lifestyle photo</Text>
                <Upload.Dragger {...uploadProps} style={{ marginTop: 12 }}>
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Drop a living room or bedroom setup
                  </p>
                  <p className="ant-upload-hint">
                    High-resolution JPG/PNG up to 25 MB
                  </p>
                </Upload.Dragger>
                {environmentPreviewNode}
                <Button type="link" className="px-0 mt-2" onClick={() => setShowPhotoModal(true)}>
                  Prefer using your camera or gallery? Click here.
                </Button>
              </div>
              <div>
                <Text strong>2. Choose a swatch</Text>
                <Search
                  allowClear
                  enterButton="Search"
                  placeholder="Search swatches by name or description"
                  className="mt-2"
                  onSearch={handleSwatchSearch}
                />
                <List
                  loading={swatchLoading}
                  grid={{ gutter: 16, xs: 1, sm: 2 }}
                  dataSource={swatches}
                  className="mt-3"
                  locale={{ emptyText: 'No swatches found' }}
                  renderItem={(swatch) => {
                    const isSelected = swatch.id === selectedSwatch;
                    return (
                      <List.Item>
                        <Card
                          hoverable
                          onClick={() => setSelectedSwatch(swatch.id)}
                          style={{
                            borderColor: isSelected ? '#1890ff' : undefined,
                            boxShadow: isSelected ? '0 0 0 2px rgba(24,144,255,0.2)' : undefined,
                            minHeight: 260,
                          }}
                        >
                          <Image
                            src={swatch.blob_url}
                            alt={swatch.name}
                            preview={false}
                            style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8 }}
                          />
                          <Title level={5} style={{ marginTop: 12, marginBottom: 4 }}>
                            {swatch.name}
                          </Title>
                          <Paragraph type="secondary" ellipsis={{ rows: 2 }}>
                            {swatch.description}
                          </Paragraph>
                          <Button
                            type="primary"
                            block
                            icon={<PlayCircleOutlined />}
                            loading={quickTrySwatch === swatch.id}
                            style={{ marginTop: 12 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleCardTryOn(swatch.id);
                            }}
                          >
                            {environmentFile.length ? 'Tryout' : 'Upload photo to Tryout'}
                          </Button>
                        </Card>
                      </List.Item>
                    );
                  }}
                />
                <Pagination
                  className="mt-2 text-right"
                  current={swatchPage}
                  pageSize={swatchPageSize}
                  total={swatchTotal}
                  showSizeChanger
                  onChange={(page, size) => {
                    setSwatchPage(page);
                    setSwatchPageSize(size);
                  }}
                />
              </div>
              <div className="w-100">
                <Text strong>3. Optional guidance for the AI</Text>
                <TextArea
                  rows={4}
                  className="mt-2"
                  placeholder="E.g., Focus on the sofa cushions and keep ambient lighting warm."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>
              <div>
                <Text strong>4. Choose an agent tag (optional)</Text>
                <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                  <Tag.CheckableTag
                    checked={!agentTag}
                    onChange={() => setAgentTag('')}
                    key="default-agent-tag"
                  >
                    Default
                  </Tag.CheckableTag>
                  {agentsLoading ? (
                    <Skeleton.Button active size="small" style={{ width: 80 }} />
                  ) : (
                    availableAgentTags.length > 0 ? (
                      availableAgentTags.map((tag) => (
                        <Tag.CheckableTag
                          key={tag}
                          checked={agentTag === tag}
                          onChange={() => setAgentTag(tag)}
                        >
                          {tag}
                        </Tag.CheckableTag>
                      ))
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        No agent tags configured yet.
                      </Text>
                    )
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Tags route the request to an agent-specific system prompt. Leave on Default to use global settings.
                </Text>
              </div>
              <Button
                type="primary"
                size="large"
                icon={processing ? <LoadingOutlined /> : <PlayCircleOutlined />}
                onClick={handleTryOn}
                loading={processing}
                block
              >
                Generate Overlay
              </Button>
            </Space>
          </Card>
        </Col>

        <Col xs={24} xl={10}>
          <Card
            title="Live Preview"
            className="shadow-sm"
            bodyStyle={{ padding: '24px', minHeight: 400 }}
          >
            {resultPreview ? (
              <Image
                src={resultPreview}
                alt="Preview"
                style={{ width: '100%', borderRadius: 16 }}
              />
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 320 }}>
                <CheckCircleTwoTone twoToneColor="#d9d9d9" style={{ fontSize: 48, marginBottom: 12 }} />
                <Paragraph type="secondary" className="text-center mb-0">
                  Your AI overlay will appear here once generated.
                </Paragraph>
              </div>
            )}
            {jobMeta && (
              <>
                <Alert
                  className="mt-3"
                  type="success"
                  showIcon
                  message={`Job ${jobMeta.job_id || 'complete'}`}
                  description={`Model ${jobMeta.model || 'custom'} · Status ${jobMeta.status}`}
                />
                <div className="mt-2">
                  <Tag color="purple">Agent Tag: {jobMeta.agent_tag || 'Default'}</Tag>
                </div>
                <Button className="mt-3" onClick={() => setDebugVisible(true)}>
                  View AI Processing Debug
                </Button>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm mt-4">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Title level={4}>Pipeline</Title>
            <Timeline
              items={[
                {
                  color: 'blue',
                  children: 'Normalize lighting & detect planes',
                },
                {
                  color: 'green',
                  children: 'Create fabric mask aligned to furniture',
                },
                {
                  color: 'purple',
                  children: 'Blend swatch with OpenAI vision model + system prompt',
                },
                {
                  color: 'gray',
                  children: 'Persist composited image to the studio library',
                },
              ]}
            />
          </Col>
          <Col xs={24} md={12}>
            <Title level={4}>Tips</Title>
              <Paragraph type="secondary">
              Capture well-lit scenes and ensure furniture is fully visible. The
              AI will honor your system prompt plus any additional instructions
              you provide above.
            </Paragraph>
          </Col>
        </Row>
      </Card>

      <Modal
        width={720}
        open={debugVisible}
        title="AI Processing Debug"
        onCancel={() => setDebugVisible(false)}
        footer={null}
      >
        {debugPayload ? (
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            {resolvedDebugError && (
              <Paragraph type="warning">
                <strong>Error:</strong> {resolvedDebugError}
              </Paragraph>
            )}
            {resolvedDebugErrorType && (
              <Paragraph type="secondary">
                <strong>Error Type:</strong> {resolvedDebugErrorType}
              </Paragraph>
            )}
            {debugModel && (
              <Paragraph>
                <strong>Model:</strong> {debugModel}
              </Paragraph>
            )}
            <Paragraph>
              <strong>Method:</strong> {debugMethod}
            </Paragraph>
            <Paragraph>
              <strong>URL:</strong> {debugUrl}
            </Paragraph>
            <Paragraph>
              <strong>Headers</strong>
            </Paragraph>
            <pre>{JSON.stringify(debugHeaders, null, 2)}</pre>
            <Paragraph>
              <strong>Request Body</strong>
            </Paragraph>
            <pre>{JSON.stringify(debugFields, null, 2)}</pre>
            <Paragraph>
              <strong>Files</strong>
            </Paragraph>
            <pre>{JSON.stringify(debugFiles, null, 2)}</pre>
            {debugResponse && (
              <>
                <Paragraph className="mt-3">
                  <strong>Response</strong>
                </Paragraph>
                <Paragraph>
                  <strong>Status:</strong> {debugResponse.status_code ?? debugResponse.status ?? 'N/A'}
                </Paragraph>
                {(debugResponse.error || debugResponse.error_type) && (
                  <>
                    <Paragraph>
                      <strong>Engine Error</strong>
                    </Paragraph>
                    <pre>
                      {JSON.stringify(
                        {
                          error_type: debugResponse.error_type || null,
                          error: debugResponse.error || null,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </>
                )}
                <Paragraph>
                  <strong>Response Body</strong>
                </Paragraph>
                <pre>{JSON.stringify(debugResponse, null, 2)}</pre>
                {debugResponse.headers && (
                  <>
                    <Paragraph>
                      <strong>Response Headers</strong>
                    </Paragraph>
                    <pre>{JSON.stringify(debugResponse.headers, null, 2)}</pre>
                  </>
                )}
                {debugResponse.json && (
                  <>
                    <Paragraph>
                      <strong>Response JSON</strong>
                    </Paragraph>
                    <pre>{JSON.stringify(debugResponse.json, null, 2)}</pre>
                  </>
                )}
                {debugResponse.text && (
                  <>
                    <Paragraph>
                      <strong>Response Text</strong>
                    </Paragraph>
                    <pre>{debugResponse.text}</pre>
                  </>
                )}
              </>
            )}
            {failedAttempts.length > 0 && (
              <>
                <Paragraph className="mt-3">
                  <strong>Failed Attempts ({failedAttempts.length})</strong>
                </Paragraph>
                {failedAttempts.map((attempt, idx) => (
                  <div key={`failed-attempt-${idx}`} style={{ marginBottom: 16 }}>
                    <Paragraph className="mb-1">
                      <strong>
                        #{idx + 1} {attempt?.engine_path || 'engine'} · {attempt?.model || 'unknown model'}
                      </strong>
                    </Paragraph>
                    <Paragraph className="mb-1">Request</Paragraph>
                    <pre>{JSON.stringify(attempt?.request || {}, null, 2)}</pre>
                    <Paragraph className="mb-1">Response</Paragraph>
                    <pre>{JSON.stringify(attempt?.response || {}, null, 2)}</pre>
                  </div>
                ))}
              </>
            )}
            {!hasDebugTransportData && (
              <Alert
                className="mt-3"
                type="info"
                showIcon
                message="No request metadata captured for this run."
                description="This can happen when a fallback path completes without upstream request/response debug capture."
              />
            )}
          </div>
        ) : (
          <Paragraph type="secondary">
            No AI request details were returned for this job (fallback overlay used).
          </Paragraph>
        )}
      </Modal>
      <Modal
        title="Existing try-on detected"
        open={existingModalVisible}
        onCancel={() => {
          setExistingModalVisible(false);
          setPendingForce(null);
        }}
        footer={[
          <Button key="use-existing" onClick={handleUseExistingResult}>
            Use existing
          </Button>,
          <Button key="reapply" type="primary" onClick={handleConfirmReapply}>
            Reapply & overwrite
          </Button>,
        ]}
      >
        {existingTryOn ? (
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Text strong>Original</Text>
              <Image
                src={existingTryOn.original_url}
                alt="Original lifestyle"
                style={{ width: '100%', borderRadius: 12, marginTop: 8 }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Text strong>Overlay</Text>
              <Image
                src={existingTryOn.overlay_url}
                alt="Existing overlay"
                style={{ width: '100%', borderRadius: 12, marginTop: 8 }}
              />
              <Paragraph className="mt-2 mb-0">
                {existingTryOn.swatch_name || 'Selected swatch'} ·{' '}
                {existingTryOn.updated_at ? new Date(existingTryOn.updated_at).toLocaleString() : ''}
              </Paragraph>
            </Col>
          </Row>
        ) : (
          <Paragraph type="secondary" className="mb-0">
            A stored overlay already exists for this lifestyle image and swatch.
          </Paragraph>
        )}
      </Modal>
      <Modal
        title="Select lifestyle photo"
        open={showPhotoModal}
        onCancel={() => setShowPhotoModal(false)}
        footer={null}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<CameraOutlined />}
            block
            onClick={() => {
              setShowPhotoModal(false);
              setShowCameraModal(true);
            }}
          >
            Take photo with camera
          </Button>
          <Button
            icon={<PictureOutlined />}
            block
            onClick={() => galleryInputRef.current?.click()}
          >
            Choose from gallery
          </Button>
        </Space>
      </Modal>
      <Modal
        title="Camera"
        open={showCameraModal}
        onCancel={() => setShowCameraModal(false)}
        footer={null}
        destroyOnClose
      >
        {cameraError ? (
          <Paragraph type="danger">{cameraError}</Paragraph>
        ) : (
          <>
            {!cameraPreview && (
              <video
                ref={cameraVideoRef}
                autoPlay
                playsInline
                style={{ width: '100%', borderRadius: 12, background: '#000' }}
              />
            )}
            <canvas ref={cameraCanvasRef} style={{ display: 'none' }} />
            {cameraPreview && (
              <Image
                src={cameraPreview}
                alt="Captured preview"
                style={{ width: '100%', borderRadius: 12 }}
              />
            )}
            <Space className="mt-3" style={{ width: '100%' }}>
              {!cameraPreview ? (
                <Button
                  type="primary"
                  block
                  icon={<CameraOutlined />}
                  loading={isCapturing}
                  onClick={capturePhoto}
                  disabled={!cameraStream}
                >
                  Capture photo
                </Button>
              ) : (
                <>
                  <Button block onClick={confirmCameraPhoto}>
                    Continue
                  </Button>
                  <Button
                    block
                    onClick={() => {
                      if (cameraPreview) {
                        URL.revokeObjectURL(cameraPreview);
                      }
                      setCameraPreview(null);
                      setCameraBlob(null);
                      if (cameraVideoRef.current && cameraStream) {
                        cameraVideoRef.current.srcObject = cameraStream;
                        cameraVideoRef.current.play();
                      } else if (!cameraStream) {
                        startCamera();
                      }
                    }}
                  >
                    Retake
                  </Button>
                </>
              )}
            </Space>
          </>
        )}
      </Modal>
    </div>
  );
};

export default TryBeforeYouBuy;

